import _ from 'lodash'
import { Drawer } from 'vaul'

import { useBodyScrollLock } from '@/shared/hooks/useBodyScrollLock'
import { useUserProgress } from '@/shared/hooks/useUserProgress'
import { sanitizeHtml } from '@/shared/lib/html/sanitize'
import { Badge } from '@/shared/ui/Badge'
import { DismissButton } from '@/shared/ui/DismissButton'
import { RewardBadge } from '@/shared/ui/RewardBadge'

import { ResourceTypeBadge } from '../components/ResourceTypeBadge'
import { LibraryBottomSheetFooter } from './Footer'

export const LibraryBottomSheet = ({
  id,
  thumb,
  name,
  description,
  archive_kind,
  url,
  file,
  image,
  point_reward,
  coin_reward,
  locked,
  onHide,
  onFinishResource
}) => {
  const { userProgress } = useUserProgress()
  const hasFinished = (userProgress?.finished_documents ?? []).includes(id)
  const hasRewards = (point_reward ?? 0) > 0 || (coin_reward ?? 0) > 0

  useBodyScrollLock(true)

  return (
    <Drawer.Root open onOpenChange={open => !open && onHide()} dismissible>
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 z-sheet-backdrop bg-black/55" />
        <Drawer.Content className="fixed bottom-0 left-0 right-0 z-sheet mx-auto flex max-h-[90vh] w-full max-w-[400px] flex-col overflow-hidden rounded-t-2xl bg-white focus:outline-none">
          <div className="relative shrink-0 px-4 pt-3 pb-2">
            <Drawer.Handle className="mx-auto h-1 w-10 shrink-0 rounded-full bg-gray-200" />
            <DismissButton
              onClick={onHide}
              aria-label="Fechar"
              className="absolute right-3 top-2"
            />
          </div>

          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain">
            {_.get(thumb, 'file.url') && (
              <img
                src={_.get(thumb, 'file.url')}
                alt={name || ''}
                className="mx-auto mb-4 block h-[200px] w-full rounded-[var(--radius-card-gamification)] object-cover px-4"
              />
            )}

            <div className="flex flex-col gap-3 px-4 pb-4">
              <div className="flex items-center gap-1.5">
                <ResourceTypeBadge kind={archive_kind} />
                {locked && (
                  <Badge variant="locked" size="sm" showIcon>
                    Bloqueado
                  </Badge>
                )}
                {hasFinished && !locked && (
                  <Badge variant="success" size="sm" showIcon>
                    Concluído
                  </Badge>
                )}
              </div>

              <Drawer.Title asChild>
                <h2 className="m-0 text-lg font-bold leading-tight text-gray-900">
                  {name}
                </h2>
              </Drawer.Title>

              {hasRewards && !hasFinished && !locked && (
                <div className="rounded-xl bg-[#fff8e1] px-3 py-2">
                  <p className="m-0 mb-1 text-2xs font-semibold text-gray-600">
                    Você receberá:
                  </p>
                  <RewardBadge.Group>
                    {point_reward > 0 && (
                      <RewardBadge type="xp" value={point_reward} variant="inline" />
                    )}
                    {coin_reward > 0 && (
                      <RewardBadge type="coins" value={coin_reward} variant="inline" />
                    )}
                  </RewardBadge.Group>
                </div>
              )}

              {hasRewards && hasFinished && (
                <div className="rounded-xl bg-[#e8f5e9] px-3 py-2">
                  <Badge variant="success" size="sm" showIcon>
                    Recompensa recebida
                  </Badge>
                </div>
              )}

              {description && (
                <div
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(description) }}
                  className="text-sm leading-relaxed text-gray-600"
                />
              )}
            </div>
          </div>

          <div className="shrink-0 border-t border-gray-100 pt-2">
            <LibraryBottomSheetFooter
              archive_kind={archive_kind}
              url={url}
              file={file}
              image={image}
              hasFinished={hasFinished}
              locked={locked}
              onFinishResource={onFinishResource}
            />
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  )
}
